import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { UtilityFunctions } from '../../utils/UtilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from "redux";
import { actionCreators, State } from "../../store";
import { cookieService, userService } from '../../_services';
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';
import SuccessToast from '../../components/successToast';
import { checkData } from "../../utils/checkData";
import { api } from "../../middleware/api";
import { PositionOptions } from 'mapbox-gl';
import { useWhitelabel } from '../../context/WhitelabelContext';
import "../../styles/mploiBrand.css";

// Styles object - keeping consistent with ARAffiliates
const styles = {
    container: {
        backgroundColor: '#F2F2F2',
        minHeight: '100vh',
    },
    topBar: {
        background: '#FFFFFF',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
    },
    topBarTitle: {
        color: '#0D0C0C',
        margin: 0,
        fontSize: '24px',
        fontWeight: '500',
    },
    blueBox: {
        background: '#FFFFFF',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
    },
    whiteText: {
        color: '#0D0C0C',
    },
    mutedText: {
        color: 'rgba(0, 0, 0, 0.6)',
    },
    formGroup: {
        marginBottom: '24px',
    },
    formLabel: {
        color: '#0D0C0C',
        fontSize: '16px',
        marginBottom: '8px',
        display: 'block',
    },
    formInput: {
        backgroundColor: '#FFFFFF',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '4px',
        padding: '12px 16px',
        color: '#0D0C0C',
        width: '100%',
        maxWidth: '400px',
        fontSize: '16px',
    },
    formError: {
        color: '#ff6b6b',
        fontSize: '14px',
        marginTop: '5px',
    },
    formHint: {
        color: 'rgba(0, 0, 0, 0.6)',
        fontSize: '14px',
        marginTop: '5px',
    },
    button: {
        padding: '10px 20px',
        borderRadius: '4px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '600',
        transition: 'all 0.3s ease',
    },
    primaryButton: {
        backgroundColor: '#FF4A0A',
        color: '#0D0C0C',
    },
    secondaryButton: {
        backgroundColor: '#FFFFFF',
        color: '#0D0C0C',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        marginRight: '10px',
    },
    dangerButton: {
        backgroundColor: 'rgba(255, 99, 71, 0.2)',
        color: '#ff6347',
        border: '1px solid rgba(255, 99, 71, 0.3)',
    },
    backButton: {
        backgroundColor: '#FFFFFF',
        color: '#0D0C0C',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    optionCard: {
        backgroundColor: '#FFFFFF',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '15px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
    },
    selectedOptionCard: {
        border: '1px solid #FF4A0A',
        backgroundColor: 'rgba(255, 74, 10, 0.1)',
    },
    optionTitle: {
        color: '#0D0C0C',
        fontSize: '18px',
        fontWeight: '600',
        marginBottom: '5px',
    },
    optionDescription: {
        color: 'rgba(0, 0, 0, 0.75)',
        fontSize: '14px',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(13, 12, 12, 0.45)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: '#F2F2F2',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        padding: '30px',
        maxWidth: '500px',
        width: '90%',
    },
    modalTitle: {
        color: '#0D0C0C',
        fontSize: '20px',
        marginBottom: '20px',
        textAlign: 'center',
    },
    modalText: {
        color: 'rgba(0, 0, 0, 0.75)',
        fontSize: '16px',
        marginBottom: '25px',
        textAlign: 'center',
    },
    modalButtons: {
        display: 'flex',
        justifyContent: 'center',
        gap: '15px',
    },
    saveButton: {
        backgroundColor: '#FF4A0A',
        color: '#0D0C0C',
        padding: '10px 20px',
        borderRadius: '4px',
        border: 'none',
        fontWeight: '600',
        cursor: 'pointer',
        fontSize: '16px',
        transition: 'all 0.3s ease',
        marginTop: '20px',
    },
    infoBox: {
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        border: '1px solid rgba(0, 123, 255, 0.3)',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
    },
    infoTitle: {
        color: '#0D0C0C',
        fontSize: '18px',
        marginBottom: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    infoText: {
        color: 'rgba(0, 0, 0, 0.75)',
    },
};

const ARAffiliateSetting = (props) => {
    const dispatch = useDispatch();
    const state = useSelector((state) => state);
    const [userData, setUserData] = useState(null);
    const { setUnreadNotificationCount } = bindActionCreators(actionCreators, dispatch);
    
    // Whitelabel branding
    const whitelabel = useWhitelabel();
    const brandName = whitelabel?.brandName || 'MPLOI';
    const referralBaseUrl = whitelabel?.referralBaseUrl || 'https://core.mploi.com/r/';
    const wlLogoSrc = whitelabel?.logoSrc;
    const user = cookieService?.getCookie(STRING_CONSTANTS?.cookieStrings?.user);
    const isUserLoggedIn = cookieService.getCookie(STRING_CONSTANTS.cookieStrings.token);
    
    // Form state
    const [referralCode, setReferralCode] = useState('');
    const [originalCode, setOriginalCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [payoutMethod, setPayoutMethod] = useState('paypal');
    const [paypalEmail, setPaypalEmail] = useState('');
    const [paypalEmailError, setPaypalEmailError] = useState('');
    const [payId, setPayId] = useState('');
    const [payIdError, setPayIdError] = useState('');
    const [registeredForGST, setRegisteredForGST] = useState(false);

    const { resetState, closeOnlineOfflineSocket } = bindActionCreators(
      actionCreators,
      dispatch
    );
    
    const handleLogout = (e) => {
      e.preventDefault();
      closeOnlineOfflineSocket(true);
      userService
        .logout()
        .then((res) => {
          api
            .deleteApi("device-token/", {
              deviceType: "WEB",
              token: `${cookieService.getCookie("fcm")}`,
            })
            .then((res) => {
              cookieService.removeAllCookies();
              props.history.push(STRING_CONSTANTS.navigationString.login);
              resetState();
            })
            .catch((e) => {
              cookieService.removeAllCookies();
              props.history.push(STRING_CONSTANTS.navigationString.login);
              resetState();
            });
        })
        .catch((err) => {
          checkData(props, "/", err);
        });
    };

    // Fetch user data and settings from API
    const fetchUserSettings = async () => {
        setIsLoading(true);
        try {
            // Get user data first
            const userData = await userService.getUserData();
            cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, userData);
            setUnreadNotificationCount(userData?.user?.unreadNotificationCount);
            setUserData(userData);

            
            // Get commission settings
            // Get referrals data
            const affiliateResponse = await userService.aiRequest({
                method: 'get-affiliate-data',
                data: {  }  // Limit to 50 most recent interactions
            });
            if (affiliateResponse && affiliateResponse.data) {
                const data = affiliateResponse.data;

                setPayId(affiliateResponse.data.settings?.payidId || '');
                setPaypalEmail(affiliateResponse.data.settings?.paypalId || '');
                setPayoutMethod(affiliateResponse.data.settings?.paymentMode || 'paypal');
                setRegisteredForGST(affiliateResponse.data.settings?.isGstRegistered || false);
                setReferralCode(data.code);
                setOriginalCode(data.code);
                if (affiliateResponse.data.payoutMethod) {
                    setPayoutMethod(affiliateResponse.data.payoutMethod);
                    
                    if (affiliateResponse.data.payoutMethod === 'paypal' && affiliateResponse.data.paypalId) {
                        setPaypalEmail(affiliateResponse.data.paypalId);
                    } else if (affiliateResponse.data.payoutMethod === 'payid' && affiliateResponse.data.payId) {
                        setPayId(affiliateResponse.data.payId);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching user settings:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isUserLoggedIn) {
            fetchUserSettings();
        }
    }, []);
    
    // Check for changes to enable/disable save button
    useEffect(() => {
        setHasChanges(referralCode !== originalCode);
    }, [referralCode, originalCode]);

    const handleReferralCodeChange = (e) => {
        const newCode = e.target.value.trim();
        setReferralCode(newCode);
        
        // Validate code
        if (newCode.length < 4 && newCode.length > 0) {
            setCodeError('Code must be at least 4 characters');
        } else if (newCode.length > 10) {
            setCodeError('Code cannot exceed 10 characters');
        } else {
            setCodeError('');
        }
    };

    const handleSaveChanges = () => {
        // Validate before saving
        if (referralCode.length < 4 || referralCode.length > 10) {
            setCodeError('Code must be between 4-10 characters');
            return;
        }
        
        // If code changed, show confirmation modal
        if (referralCode !== originalCode) {
            setShowConfirmModal(true);
            return;
        }
        
        // Otherwise just save changes
        saveSettings();
    };
    
    const saveSettings = async () => {
        setIsLoading(true);
        try {
            // Prepare data for API
            const settingsData = {
                referralCode: referralCode,
                payoutMethod: payoutMethod,
                paypalEmail: paypalEmail,
                payidEmail: payId,
                isGstRegistered: registeredForGST,
                commissionSplit: 'full_commission',
            };
            
            // Call API to save settings using save-affiliate-data endpoint
            const response = await userService.aiRequest({
                method: 'save-affiliate-data',
                data: settingsData,
            });
            console.log("Settings saved successfully:", response);
            
            // Update original code to reflect saved state
            setOriginalCode(referralCode);
            
            SuccessToast("Success", "Your affiliate settings have been updated!");
            setShowConfirmModal(false);
        } catch (error) {
            console.error("Error saving settings:", error);
            SuccessToast("Error", "Failed to save settings. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoBack = () => {
        props.history.push('/ar/affiliate/dashboard');
    };

    return (
        <section className="main-dashboard-wrap mploiBrand" style={styles.container}>
            <Helmet>
                <title>{brandName}: Affiliate Settings</title>
            </Helmet>
            <div id="wrapper" style={{ height: "100vh" }}>
                {/* Sidebar - same as ARAffiliates */}
                <ul className={`navbar-nav bg-gradient-primary sidebar sidebar-dark accordion ${state.common.showSidebarOnMobileView ? "active" : ""}`} id="accordionSidebar">
                    <div>
                        <Link style={{ pointerEvents: "none" }} className="sidebar-brand d-flex align-items-center justify-content-center" to="#">
                            <img src={wlLogoSrc || (UtilityFunctions.getImageURL() + "db-logo.svg")} alt="Logo" />
                        </Link>
                        {!user?.user?.isAccountsSignup && <li className="nav-item">
                            <Link className="nav-link" to="/ar/affiliate/dashboard">
                                <img src={UtilityFunctions.getImageURL() + "home-icon-blank-white.svg"} alt="" />
                                <img src={UtilityFunctions.getImageURL() + "home-icon-fill-green.svg"} alt="" />
                            </Link>
                        </li>}
                    </div>

                    <div>
                        <li className='d-grid justify-content-center gap-2 text-white'>
                            <span style={{ cursor: "pointer" }}
                                onClick={handleLogout}
                            >
                                Logout
                            </span>
                        </li>
                    </div>
                </ul>

                {/* Content Wrapper */}
                <div id="content-wrapper" className="d-flex flex-column">
                    <div id="content">
                        {/* Top Bar with Back Button */}
                        <div style={styles.topBar}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <button 
                                    style={{...styles.button, ...styles.backButton}}
                                    onClick={handleGoBack}
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M15 8H1M1 8L8 15M1 8L8 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Back
                                </button>
                                <h1 style={styles.topBarTitle}>Affiliate Settings</h1>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="container-fluid">
                            {isLoading ? (
                                <div style={styles.blueBox}>
                                    <p style={styles.whiteText}>Loading your settings...</p>
                                </div>
                            ) : (
                                <>
                                    {/* Referral Code Section */}
                                    <div style={styles.blueBox}>
                                        <h3 style={{...styles.whiteText, marginBottom: '20px'}}>
                                            Your Referral Code
                                        </h3>
                                        
                                        <div style={styles.formGroup}>
                                            <input 
                                                type="text"
                                                style={styles.formInput}
                                                value={referralCode}
                                                onChange={handleReferralCodeChange}
                                                placeholder="Enter your custom code"
                                            />
                                            {codeError && (
                                                <p style={styles.formError}>{codeError}</p>
                                            )}
                                            <p style={styles.formHint}>
                                                Your referral code must be between 4-10 characters. 
                                                This will appear in your affiliate URL: {referralBaseUrl}{referralCode || '[code]'}
                                            </p>
                                        </div>
                                        
                                        <div style={styles.infoBox}>
                                            <h4 style={styles.infoTitle}>Important Note</h4>
                                            <p style={styles.infoText}>
                                                Changing your referral code will create a new affiliate link. Your old link will no longer work, 
                                                and you'll need to update any marketing materials where you've shared your previous link.
                                            </p>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'left', marginTop: '20px' }}>
                                            <button 
                                                style={{...styles.button, ...styles.primaryButton}}
                                                onClick={handleSaveChanges}
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>

                                    {/* Payout Method Section */}
                                    <div style={styles.blueBox}>
                                        <h3 style={{...styles.whiteText, marginBottom: '20px'}}>
                                            Payout Method
                                        </h3>
                                        
                                        <p style={styles.mutedText}>
                                            Choose how you'd like to receive your affiliate commission payments.
                                        </p>
                                        
                                        <div style={{ marginTop: '20px' }}>
                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>
                                                    Payout Method
                                                </label>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '10px' }}>
                                                    <div 
                                                        style={{
                                                            ...styles.optionCard,
                                                            ...(payoutMethod === 'paypal' ? styles.selectedOptionCard : {}),
                                                            padding: '15px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '15px'
                                                        }}
                                                        onClick={() => setPayoutMethod('paypal')}
                                                    >
                                                        <div style={{ 
                                                            width: '24px', 
                                                            height: '24px', 
                                                            borderRadius: '50%', 
                                                            border: '2px solid ' + (payoutMethod === 'paypal' ? '#FF4A0A' : 'rgba(0, 0, 0, 0.15)'),
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center'
                                                        }}>
                                                            {payoutMethod === 'paypal' && (
                                                                <div style={{ 
                                                                    width: '12px', 
                                                                    height: '12px', 
                                                                    borderRadius: '50%', 
                                                                    backgroundColor: '#FF4A0A' 
                                                                }}></div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <h4 style={styles.optionTitle}>PayPal</h4>
                                                            <p style={styles.optionDescription}>Receive payments to your PayPal account</p>
                                                        </div>
                                                    </div>
                                                    
                                                    <div 
                                                        style={{
                                                            ...styles.optionCard,
                                                            ...(payoutMethod === 'payid' ? styles.selectedOptionCard : {}),
                                                            padding: '15px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '15px'
                                                        }}
                                                        onClick={() => setPayoutMethod('payid')}
                                                    >
                                                        <div style={{ 
                                                            width: '24px', 
                                                            height: '24px', 
                                                            borderRadius: '50%', 
                                                            border: '2px solid ' + (payoutMethod === 'payid' ? '#FF4A0A' : 'rgba(0, 0, 0, 0.15)'),
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center'
                                                        }}>
                                                            {payoutMethod === 'payid' && (
                                                                <div style={{ 
                                                                    width: '12px', 
                                                                    height: '12px', 
                                                                    borderRadius: '50%', 
                                                                    backgroundColor: '#FF4A0A' 
                                                                }}></div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <h4 style={styles.optionTitle}>PayID</h4>
                                                            <p style={styles.optionDescription}>Receive payments using your email or phone PayID</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {payoutMethod === 'paypal' && (
                                                <div style={{...styles.formGroup, marginTop: '20px'}}>
                                                    <label style={styles.formLabel}>
                                                        PayPal Email Address
                                                    </label>
                                                    <input 
                                                        type="email"
                                                        style={styles.formInput}
                                                        value={paypalEmail}
                                                        onChange={(e) => setPaypalEmail(e.target.value)}
                                                        placeholder="your-email@example.com"
                                                    />
                                                    {paypalEmailError && (
                                                        <p style={styles.formError}>{paypalEmailError}</p>
                                                    )}
                                                </div>
                                            )}
                                            
                                            {payoutMethod === 'payid' && (
                                                <div style={{...styles.formGroup, marginTop: '20px'}}>
                                                    <label style={styles.formLabel}>
                                                        PayID (Email or Phone)
                                                    </label>
                                                    <input 
                                                        type="text"
                                                        style={styles.formInput}
                                                        value={payId}
                                                        onChange={(e) => setPayId(e.target.value)}
                                                        placeholder="email@example.com or +61412345678"
                                                    />
                                                    {payIdError && (
                                                        <p style={styles.formError}>{payIdError}</p>
                                                    )}
                                                    <p style={styles.formHint}>
                                                        Enter either your email address or phone number associated with your PayID
                                                    </p>
                                                </div>
                                            )}
                                            {/* Add an is GST registered boolean switch */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                                                <input 
                                                    type="checkbox" 
                                                    id="is-gst-registered" 
                                                    name="is-gst-registered" 
                                                    style={{ width: '20px', height: '20px' }} 
                                                    checked={registeredForGST}
                                                    onChange={(e) => setRegisteredForGST(e.target.checked)}
                                                />
                                                <label htmlFor="is-gst-registered" style={styles.mutedText}>
                                                    I am registered for GST
                                                </label>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'left', marginTop: '20px' }}>
                                            <button 
                                                style={{...styles.button, ...styles.primaryButton}}
                                                onClick={handleSaveChanges}
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>

                                    {/* How you get paid */}
                                    <div style={styles.blueBox}>
                                        <h3 style={{...styles.whiteText, marginBottom: '20px'}}>
                                            How you get paid
                                        </h3>

                                        <p style={styles.mutedText}>
                                            You earn 50% of each referral's first monthly payment, ex GST. Your
                                            referral pays the standard price — nothing about your link changes what
                                            they are charged. The commission is recorded the moment their first
                                            payment clears, and is paid out in the next payout run.
                                        </p>

                                        <p style={styles.mutedText}>
                                            It is a one-off commission on the first month, not a trailing one. If a
                                            referral cancels before paying, no commission is earned.
                                        </p>

                                        <div style={{ display: 'flex', justifyContent: 'left', marginTop: '20px' }}>
                                            <button 
                                                style={{...styles.button, ...styles.primaryButton}}
                                                onClick={handleSaveChanges}
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>

                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h3 style={styles.modalTitle}>Change Referral Code?</h3>
                        <p style={styles.modalText}>
                            Are you sure you want to change your referral code from <strong>{originalCode}</strong> to <strong>{referralCode}</strong>?
                            <br /><br />
                            Your old affiliate link will no longer work, and you'll need to update any marketing materials where you've shared your previous link.
                        </p>
                        <div style={styles.modalButtons}>
                            <button 
                                style={{...styles.button, ...styles.secondaryButton}}
                                onClick={() => setShowConfirmModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                style={{...styles.button, ...styles.primaryButton}}
                                onClick={saveSettings}
                            >
                                Yes, Change Code
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ARAffiliateSetting;